import React from "react";
import { Link, useParams } from "react-router-dom";
import { FaArrowLeft, FaArrowRight, FaCheckCircle } from "react-icons/fa";

import { getServiceBySlug, services } from "../data/servicesData";
import Seo from "../components/Seo";

const serviceSeoKeywordMap = {
  "academic-management": [
    "academic management software",
    "curriculum management software",
    "school timetable software",
    "class subject management system",
    "teacher allocation software",
  ],
  "admissions-enquiries": [
    "admission management system",
    "school admission software",
    "online admission form software",
    "student enquiry management software",
    "enrollment management system",
  ],
  "attendance-roll-calls": [
    "attendance management system",
    "school attendance software",
    "student attendance tracking",
    "roll call management software",
    "biometric attendance for schools",
  ],
  "fee-management-payments": [
    "fee management software",
    "school fee collection software",
    "online fee payment system",
    "school billing software",
    "fee receipt management system",
  ],
  "exams-assessments": [
    "exam management system",
    "school assessment software",
    "online exam software for schools",
    "report card management software",
    "question bank software",
  ],
  "learning-management-lms": [
    "school LMS software",
    "learning management system for schools",
    "online learning platform for schools",
    "assignment management software",
    "digital classroom software",
  ],
  "notifications-communication": [
    "parent communication app",
    "school communication software",
    "school notification system",
    "SMS software for schools",
    "parent teacher communication platform",
  ],
  "analytics-reports": [
    "school analytics dashboard",
    "school reporting software",
    "student performance analytics",
    "education analytics software",
    "school dashboard software",
  ],
  "transport-logistics": [
    "school transport management software",
    "school bus tracking system",
    "transport route management software",
    "student pickup drop tracking",
    "vehicle management software for schools",
  ],
  "integrations-cloud": [
    "cloud based school management software",
    "school ERP integration",
    "cloud school ERP",
    "secure school management system",
    "payment gateway integration for schools",
  ],
};

const ServiceDetail = () => {
  const { serviceSlug } = useParams();
  const service = getServiceBySlug(serviceSlug);

  if (!service) {
    return (
      <div className="min-h-screen bg-[linear-gradient(180deg,_#fffdf7_0%,_#eff6ff_100%)] px-6 py-16">
        <Seo
          title="Service Not Found | ClassNX"
          description="The requested ClassNX service page could not be found."
          canonical="/services"
          robots="noindex, follow"
        />
        <div className="mx-auto max-w-3xl rounded-[2rem] border border-slate-200 bg-white p-8 text-center shadow-[0_24px_60px_-40px_rgba(15,23,42,0.45)] sm:p-12">
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-amber-600">Service not found</p>
          <h1 className="mt-4 text-3xl font-extrabold text-slate-900">That service page does not exist.</h1>
          <p className="mt-4 text-sm leading-7 text-slate-600 sm:text-base">
            Return to the services page and choose one of the available ClassNX modules.
          </p>
          <Link
            to="/services"
            className="mx-auto mt-8 inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-3 font-semibold text-white transition hover:bg-slate-800"
          >
            <FaArrowLeft className="text-sm" />
            Back to Services
          </Link>
        </div>
      </div>
    );
  }

  const Icon = service.icon;
  const relatedServices = services
    .filter((item) => item.slug !== service.slug)
    .slice(0, 3);
  const serviceSummary =
    service.desc.charAt(0).toLowerCase() + service.desc.slice(1);
  const pageDescription = `ClassNX ${service.title.toLowerCase()} software helps schools ${serviceSummary}`;
  const serviceKeywords = Array.from(
    new Set([
      "school management software",
      "school ERP software",
      "school management system",
      "student information system",
      "school administration software",
      `${service.title.toLowerCase()} software`,
      `${service.title.toLowerCase()} system`,
      ...(serviceSeoKeywordMap[service.slug] || []),
    ])
  );

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(251,191,36,0.16),_transparent_24%),linear-gradient(180deg,_#fffdf8_0%,_#eff6ff_55%,_#f7fff9_100%)] px-6 py-8 sm:py-10">
      <Seo
        title={`${service.title} Software | ClassNX School ERP`}
        description={pageDescription}
        canonical={`/services/${service.slug}`}
        keywords={serviceKeywords}
      />
      <div className="mx-auto max-w-6xl">
        <Link
          to="/services"
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-amber-200 hover:text-amber-700"
        >
          <FaArrowLeft className="text-xs" />
          Back to all services
        </Link>

        <section className="relative mt-6 overflow-hidden rounded-[2.2rem] border border-white/70 bg-[radial-gradient(circle_at_top_left,_rgba(251,191,36,0.16),_transparent_24%),radial-gradient(circle_at_bottom_right,_rgba(16,185,129,0.12),_transparent_28%),linear-gradient(135deg,_#0f172a_0%,_#1e3a8a_46%,_#0f766e_100%)] px-7 py-10 text-white shadow-[0_30px_70px_-35px_rgba(15,23,42,0.72)] sm:px-10 sm:py-12">
          <div className="pointer-events-none absolute inset-0 opacity-30" aria-hidden="true">
            <div className="absolute -left-8 top-10 h-40 w-40 rounded-full bg-amber-300 blur-3xl" />
            <div className="absolute right-0 top-0 h-56 w-56 rounded-full bg-cyan-400 blur-3xl" />
          </div>
          <div className="relative grid gap-8 lg:grid-cols-[1.1fr_0.6fr] lg:items-end">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-amber-200">{service.eyebrow}</p>
              <div className="mt-4 flex items-start gap-4">
                <div className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-[1.4rem] bg-gradient-to-br ${service.color} text-slate-900 shadow-[0_16px_36px_-24px_rgba(15,23,42,0.85)]`}>
                  <Icon className="text-3xl" />
                </div>
                <div>
                  <h1 className="text-4xl font-extrabold leading-tight sm:text-5xl">{service.title}</h1>
                  <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-200 sm:text-base">
                    {service.overview}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-[1.7rem] border border-white/12 bg-white/10 p-5 backdrop-blur-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-200">Why it matters</p>
              <p className="mt-3 text-sm leading-7 text-white/90">{service.spotlight}</p>
              <div className="mt-5 h-px bg-white/10" />
              <p className="mt-5 text-xs font-semibold uppercase tracking-[0.22em] text-slate-200">Best suited for</p>
              <p className="mt-2 text-sm leading-7 text-white/90">{service.idealFor}</p>
            </div>
          </div>
        </section>

        <section className="mt-8 grid gap-6 lg:grid-cols-[1fr_0.78fr]">
          <div className="rounded-[2rem] border border-slate-200/80 bg-white p-7 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.45)] sm:p-8">
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-amber-600">Key capabilities</p>
            <h2 className="mt-3 text-3xl font-extrabold text-slate-900">What this module helps your team do</h2>
            <div className="mt-8 grid gap-4">
              {service.capabilities.map((capability) => (
                <div key={capability} className="flex gap-4 rounded-[1.4rem] border border-slate-200 bg-slate-50 px-4 py-4">
                  <div className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${service.accent} text-white`}>
                    <FaCheckCircle className="text-sm" />
                  </div>
                  <p className="text-sm leading-7 text-slate-700 sm:text-base">{capability}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-[2rem] border border-slate-200/80 bg-white p-7 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.45)] sm:p-8">
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-amber-600">Expected outcomes</p>
              <div className="mt-6 grid gap-4">
                {service.outcomes.map((outcome) => (
                  <div key={outcome} className="rounded-[1.4rem] bg-slate-900 px-5 py-4 text-white">
                    <p className="text-lg font-bold">{outcome}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[2rem] border border-slate-200/80 bg-[linear-gradient(180deg,_#ffffff_0%,_#fff7ed_100%)] p-7 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.45)] sm:p-8">
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-amber-600">Next step</p>
              <h2 className="mt-3 text-2xl font-extrabold text-slate-900">Want to see this workflow in action?</h2>
              <p className="mt-3 text-sm leading-7 text-slate-600">
                Return to the services page and open the Request Demo flow. We can tailor the walkthrough around {service.title.toLowerCase()} and the adjacent workflows your school uses most.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-3 font-semibold text-white transition hover:bg-slate-800"
                >
                  Request from Services
                  <FaArrowRight className="text-sm" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-8 rounded-[2rem] border border-slate-200/80 bg-white p-7 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.45)] sm:p-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-amber-600">Explore more</p>
              <h2 className="mt-3 text-2xl font-extrabold text-slate-900">Other ClassNX modules</h2>
            </div>
            <Link to="/services" className="text-sm font-semibold text-amber-700 transition hover:text-amber-800">
              View all services
            </Link>
          </div>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {relatedServices.map((item) => {
              const RelatedIcon = item.icon;

              return (
                <Link
                  key={item.slug}
                  to={`/services/${item.slug}`}
                  className="group rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5 transition hover:-translate-y-1 hover:border-amber-200 hover:bg-white hover:shadow-lg hover:shadow-slate-200/60"
                >
                  <div className={`flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${item.color} text-slate-900`}>
                    <RelatedIcon className="text-xl" />
                  </div>
                  <h3 className="mt-4 text-lg font-bold text-slate-900">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{item.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-amber-700 transition group-hover:translate-x-1">
                    View details
                    <FaArrowRight className="text-xs" />
                  </span>
                </Link>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ServiceDetail;
